import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import LoadingSpinner from "../components/LoadingSpinner";
import ImageModal from "../components/ImageModal";
import CreateIssueForm, { IssueCreatedBanner } from "../components/CreateIssueForm";
const CLOSE_REVIEW_EMAIL_NOTE = "Single collated email will be sent upon Close Review.";

function presetCardDomId(presetName) {
    return "preset-" + String(presetName || "").replace(/[^A-Za-z0-9_-]/g, "_");
}

const COMPLIANCE_CHECKS = [
    { key: "chair", label: "No chair behind counter" },
    { key: "earbuds", label: "No earbuds / phone use" },
    { key: "doors", label: "Doors closed" },
    { key: "uniform", label: "Uniform correct" },
    { key: "name_badge", label: "Name badge visible" },
];

const severityColor = {
    critical: "var(--red)",
    high: "#f97316",
    warning: "#eab308",
    medium: "#3b82f6",
    low: "var(--text-muted)",
    info: "var(--text-muted)",
};

function ScoreBadge({ score }) {
    if (score == null) return <span style={{ color: "var(--text-muted)" }}>--</span>;
    const color = score >= 8 ? "#22c55e" : score >= 5 ? "#eab308" : "var(--red)";
    return <span style={{ color, fontWeight: 600 }}>{score}/10</span>;
}

const statusColors = {
    open: "#ef4444", reopened: "#f97316", actioned: "#3b82f6",
    resolved: "#22c55e", false_positive: "var(--text-muted)",
};

function PresetNotesPanel({ preset, siteId, reviewerEmail }) {
    const [open, setOpen] = useState(false);
    const [notes, setNotes] = useState(preset.notes || []);
    const [newNote, setNewNote] = useState("");
    const [saving, setSaving] = useState(false);

    const recentIssues = preset.recentIssues || [];
    const badgeCount = recentIssues.length + notes.length;

    const addNote = async () => {
        const text = newNote.trim();
        if (!text || saving) return;
        setSaving(true);
        try {
            const res = await api.addPresetNote(siteId, {
                presetName: preset.presetName,
                note: text,
                createdBy: reviewerEmail,
            });
            setNotes(prev => [{ id: res.id, note: text, createdBy: reviewerEmail, createdAt: res.createdAt || new Date().toISOString() }, ...prev]);
            setNewNote("");
        } catch (err) {
            console.error("Failed to add note:", err);
        } finally {
            setSaving(false);
        }
    };

    const deleteNote = async (noteId) => {
        try {
            await api.deletePresetNote(siteId, noteId);
            setNotes(prev => prev.filter(n => n.id !== noteId));
        } catch (err) {
            console.error("Failed to delete note:", err);
        }
    };

    const formatDate = (d) => {
        try {
            const dt = new Date(d);
            const day = dt.getDate();
            const mon = dt.toLocaleString("en-AU", { month: "short" });
            return `${day} ${mon}`;
        } catch { return ""; }
    };

    return (
        <div style={{ marginTop: 6 }}>
            <button
                onClick={() => setOpen(!open)}
                style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "3px 8px", fontSize: 11, fontWeight: 600,
                    background: open ? "var(--bg-secondary)" : "transparent",
                    border: "1px solid var(--border)", borderRadius: 4,
                    cursor: "pointer", color: "var(--text-secondary)",
                }}
            >
                {open ? "\u25BE" : "\u25B8"} Notes
                {badgeCount > 0 && (
                    <span style={{
                        fontSize: 10, fontWeight: 700, padding: "0 5px",
                        background: recentIssues.some(i => i.status === "open" || i.status === "reopened")
                            ? "rgba(239,68,68,0.15)" : "var(--bg-secondary)",
                        color: recentIssues.some(i => i.status === "open" || i.status === "reopened")
                            ? "#ef4444" : "var(--text-muted)",
                        borderRadius: 8,
                    }}>
                        {badgeCount}
                    </span>
                )}
            </button>

            {open && (
                <div style={{
                    marginTop: 6, padding: "8px 10px", fontSize: 12,
                    background: "var(--bg-secondary)", borderRadius: 6,
                    border: "1px solid var(--border)",
                }}>
                    {/* Recent issues */}
                    {recentIssues.length > 0 && (
                        <div style={{ marginBottom: notes.length > 0 ? 8 : 0 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 4 }}>
                                Recent Issues (14 days)
                            </div>
                            {recentIssues.map(issue => (
                                <div key={issue.id} style={{
                                    padding: "4px 0", lineHeight: 1.4,
                                    borderBottom: "1px solid var(--border)",
                                }}>
                                    <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                                        <span style={{
                                            fontSize: 10, fontWeight: 600, flexShrink: 0,
                                            color: statusColors[issue.status] || "var(--text-muted)",
                                        }}>
                                            {issue.status}
                                        </span>
                                        <span style={{ flex: 1, fontSize: 11, fontWeight: 500 }}>{issue.title}</span>
                                        <span style={{ fontSize: 10, color: "var(--text-muted)", flexShrink: 0 }}>
                                            {formatDate(issue.firstDetectedAt)}
                                        </span>
                                    </div>
                                    {issue.comment && (
                                        <div style={{ fontSize: 10, color: "var(--text-secondary)", marginTop: 2, paddingLeft: 2 }}>
                                            <span style={{ fontWeight: 600 }}>{issue.assignedToTeam || "Raised"}</span>
                                            {issue.raisedBy ? <span style={{ color: "var(--text-muted)" }}> ({issue.raisedBy.split("@")[0]})</span> : null}
                                            : {issue.comment}
                                        </div>
                                    )}
                                    {issue.actionResponse && (
                                        <div style={{ fontSize: 10, color: statusColors[issue.status] || "var(--text-secondary)", marginTop: 2, paddingLeft: 2 }}>
                                            <span style={{ fontWeight: 600 }}>Response</span>
                                            {issue.actionedBy ? <span style={{ color: "var(--text-muted)" }}> ({issue.actionedBy.split("@")[0]})</span> : null}
                                            : {issue.actionResponse}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Notes */}
                    {notes.length > 0 && (
                        <div style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 4 }}>
                                Notes
                            </div>
                            {notes.map(n => (
                                <div key={n.id} style={{
                                    display: "flex", alignItems: "baseline", gap: 6,
                                    padding: "3px 0", lineHeight: 1.3,
                                }}>
                                    <span style={{ flex: 1, fontSize: 11 }}>{n.note}</span>
                                    <span style={{ fontSize: 10, color: "var(--text-muted)", flexShrink: 0 }}>
                                        {formatDate(n.createdAt)} — {(n.createdBy || "").split("@")[0]}
                                    </span>
                                    <button
                                        onClick={() => deleteNote(n.id)}
                                        style={{
                                            background: "none", border: "none", cursor: "pointer",
                                            color: "var(--text-muted)", fontSize: 11, padding: "0 2px",
                                        }}
                                        title="Delete note"
                                    >&times;</button>
                                </div>
                            ))}
                        </div>
                    )}

                    {recentIssues.length === 0 && notes.length === 0 && (
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>No recent issues or notes.</div>
                    )}

                    {/* Add note */}
                    <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                        <input
                            type="text"
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && addNote()}
                            placeholder="Add a note..."
                            style={{
                                flex: 1, fontSize: 11, padding: "4px 6px",
                                border: "1px solid var(--border)", borderRadius: 3,
                                background: "var(--bg-primary)", color: "var(--text-primary)",
                            }}
                        />
                        <button
                            onClick={addNote}
                            disabled={saving || !newNote.trim()}
                            style={{
                                padding: "4px 10px", fontSize: 12, fontWeight: 700,
                                background: saving || !newNote.trim() ? "var(--bg-secondary)" : "var(--primary)",
                                color: saving || !newNote.trim() ? "var(--text-muted)" : "#fff",
                                border: "none", borderRadius: 3, cursor: saving ? "default" : "pointer",
                            }}
                        >+</button>
                    </div>
                </div>
            )}
        </div>
    );
}

function ScanReviewPage() {
    const { siteId, scanHour: encodedHour } = useParams();
    const scanHour = decodeURIComponent(encodedHour);
    const location = useLocation();
    const navigate = useNavigate();
    const { accounts } = useMsal();
    const { hasPermission } = useAuth();
    const currentAccount = accounts[0];
    const canCreateIssue = hasPermission("issues.create");
    const canManageReview = hasPermission("reviews.manage");

    const [scanDetail, setScanDetail] = useState(null);
    const [reviewInfo, setReviewInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [completing, setCompleting] = useState(false);
    const [showCloseDialog, setShowCloseDialog] = useState(false);
    const [issueCount, setIssueCount] = useState(0);
    const [creatingIssueFor, setCreatingIssueFor] = useState(null);
    const [issueResults, setIssueResults] = useState({});
    const [modalImages, setModalImages] = useState(null);
    const [modalIndex, setModalIndex] = useState(0);
    const [observations, setObservations] = useState({});

    const completedRef = useRef(false);
    const issueCountRef = useRef(0);
    const saveTimerRef = useRef(null);

    const reviewerEmail = currentAccount?.username || "web_user";
    const reviewerName = currentAccount?.name || currentAccount?.username || "Web User";
    const hourParam = scanHour.replace(" ", "+");

    useEffect(() => {
        if (!scanDetail || !location.hash) return;
        const id = decodeURIComponent(location.hash.replace(/^#/, ""));
        const t = setTimeout(() => {
            const el = document.getElementById(id);
            if (!el) return;
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.style.outline = "3px solid #3b82f6";
            el.style.outlineOffset = "2px";
        }, 250);
        return () => clearTimeout(t);
    }, [scanDetail, location.hash]);

    const saveObservations = useCallback((obs) => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            api.saveObservations(siteId, hourParam, {
                observations: obs,
                reviewerEmail,
            }).catch(err => console.error("Failed to save observations:", err));
        }, 1000);
    }, [siteId, hourParam, reviewerEmail]);

    const handleObservationChange = useCallback((checkKey, field, value) => {
        setObservations(prev => {
            const updated = {
                ...prev,
                [checkKey]: { ...prev[checkKey], [field]: value }
            };
            saveObservations(updated);
            return updated;
        });
    }, [saveObservations]);

    useEffect(() => {
        const init = async () => {
            try {
                // Only start a review (create ScanReview row) if user has reviews.manage permission
                if (canManageReview) {
                    const [detail, review] = await Promise.all([
                        api.scanDetail(siteId, hourParam),
                        api.startReview(siteId, hourParam, { reviewerEmail, reviewerName }),
                    ]);
                    setScanDetail(detail);
                    setReviewInfo(review);
                } else {
                    // View-only mode — load scan detail without starting a review
                    const detail = await api.scanDetail(siteId, hourParam);
                    setScanDetail(detail);
                }

                // Load existing observations
                try {
                    const obsData = await api.getObservations(siteId, hourParam);
                    if (obsData && obsData.observations) {
                        setObservations(obsData.observations);
                    }
                } catch (e) {
                    // Non-critical — observations may not exist yet
                }
            } catch (err) {
                console.error("Failed to initialise review:", err);
            } finally {
                setLoading(false);
            }
        };
        init();

        // Best-effort abandon on tab/window close — resets to not reviewed
        const handleUnload = () => {
            if (!completedRef.current && canManageReview) {
                // Fire-and-forget — browser may not honour async fetch on unload
                api.abandonReview(siteId, hourParam, { reviewerEmail }).catch(() => {});
            }
        };
        window.addEventListener("beforeunload", handleUnload);

        // Cleanup on React unmount (SPA navigation via menu clicks etc.)
        return () => {
            window.removeEventListener("beforeunload", handleUnload);
            if (!completedRef.current && canManageReview) {
                api.abandonReview(siteId, hourParam, { reviewerEmail }).catch(() => {});
            }
        };
    }, []);  // eslint-disable-line

    const handleClose = async (markReviewed) => {
        if (completing) return;
        setShowCloseDialog(false);
        if (markReviewed === true) {
            setCompleting(true);
            try {
                await api.completeReview(siteId, hourParam, {
                    reviewerEmail,
                    issuesRaised: issueCount,
                });
                completedRef.current = true;
            } catch (err) {
                console.error("Failed to complete review:", err);
            }
        } else if (markReviewed === false) {
            // "No — keep as in progress" — leave the ScanReview row as-is
        }
        navigate("/reviews");
    };

    const handleAbandon = async () => {
        setShowCloseDialog(false);
        completedRef.current = true; // prevent beforeunload from firing again
        try {
            await api.abandonReview(siteId, hourParam, { reviewerEmail });
        } catch (err) {
            console.error("Failed to abandon review:", err);
        }
        navigate("/reviews");
    };

    const handleIssueCreated = (key, result) => {
        setIssueResults((prev) => ({ ...prev, [key]: result }));
        setCreatingIssueFor(null);
        setIssueCount((n) => {
            const next = n + 1;
            issueCountRef.current = next;
            return next;
        });
    };

    const formatScanTime = () => {
        try {
            const d = new Date(scanHour.replace(" ", "T") + ":00:00Z");
            return d.toLocaleString("en-AU", {
                weekday: "short", day: "numeric", month: "short",
                year: "numeric", hour: "numeric", minute: "2-digit",
                hour12: true, timeZone: scanDetail?.timezone || "Australia/Melbourne",
            });
        } catch {
            return scanHour;
        }
    };

    if (loading) return <LoadingSpinner message="Loading scan..." />;

    if (!scanDetail) {
        return (
            <div className="empty-state">
                <h3>Failed to load scan</h3>
                <button className="retry-btn" onClick={() => navigate("/reviews")}>Back to Reviews</button>
            </div>
        );
    }

    return (
        <div>
            {/* Sticky review bar */}
            <div style={{
                position: "sticky", top: 0, zIndex: 100,
                background: "var(--bg-primary)", borderBottom: "2px solid var(--border)",
                padding: "12px 20px", display: "flex", alignItems: "center", gap: 14,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
            }}>
                {canManageReview ? (
                    <button
                        onClick={handleAbandon}
                        style={{
                            padding: "5px 10px", fontSize: 13, cursor: "pointer",
                            background: "transparent", border: "1px solid var(--border)",
                            borderRadius: 6, color: "var(--text-muted)"
                        }}
                        title="Leave without marking this scan reviewed"
                    >
                        ← Exit without reviewing
                    </button>
                ) : (
                    <button
                        onClick={() => navigate("/reviews")}
                        style={{
                            padding: "5px 10px", fontSize: 13, cursor: "pointer",
                            background: "transparent", border: "1px solid var(--border)",
                            borderRadius: 6, color: "var(--text-muted)"
                        }}
                    >
                        ← Back to Reviews
                    </button>
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>
                        {scanDetail.storeName || siteId}
                        <span style={{ color: "var(--text-muted)", fontWeight: 400, marginLeft: 8, fontSize: 13 }}>
                            #{siteId}
                        </span>
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>{formatScanTime()}</div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {scanDetail.avgScore != null && (
                        <span style={{ fontSize: 13 }}>
                            Avg: <ScoreBadge score={scanDetail.avgScore} />
                        </span>
                    )}
                    {issueCount > 0 && (
                        <span style={{
                            fontSize: 12, fontWeight: 600, padding: "3px 10px",
                            background: "rgba(249,115,22,0.1)", color: "#f97316", borderRadius: 10
                        }}>
                            {issueCount} issue{issueCount !== 1 ? "s" : ""} raised
                        </span>
                    )}
                    {canManageReview ? (
                        <>
                            <span style={{
                                fontSize: 12, color: "#3b82f6", fontWeight: 600,
                                padding: "3px 10px", background: "rgba(59,130,246,0.1)", borderRadius: 10
                            }}>
                                In Review — {reviewerName}
                            </span>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                                <button
                                    onClick={() => setShowCloseDialog(true)}
                                    disabled={completing}
                                    style={{
                                        padding: "7px 18px", fontSize: 13, fontWeight: 600,
                                        background: completing ? "#aaa" : "#22c55e", color: "#fff",
                                        border: "none", borderRadius: 6, cursor: completing ? "default" : "pointer"
                                    }}
                                >
                                    {completing ? "Saving..." : "Close Review"}
                                </button>
                                <div style={{ fontSize: 12, color: "var(--text-muted)", maxWidth: 260, textAlign: "right" }}>
                                    {CLOSE_REVIEW_EMAIL_NOTE}
                                </div>
                            </div>
                        </>
                    ) : (
                        <span style={{
                            fontSize: 12, color: "var(--text-muted)", fontWeight: 600,
                            padding: "3px 10px", background: "var(--bg-secondary)", borderRadius: 10,
                            border: "1px solid var(--border)"
                        }}>
                            View Only
                        </span>
                    )}
                </div>
            </div>

            {/* Compliance observations */}
            <div style={{
                margin: "0 20px", padding: "12px 16px",
                background: "var(--bg-secondary)", borderRadius: 8,
                border: "1px solid var(--border)", marginTop: 16
            }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: "var(--text-primary)" }}>
                    Site Compliance Observations
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                    {COMPLIANCE_CHECKS.map(check => {
                        const obs = observations[check.key] || {};
                        const isYes = obs.isCompliant === true;
                        const isNo = obs.isCompliant === false;
                        return (
                            <div key={check.key} style={{
                                flex: "1 1 220px", minWidth: 200,
                                padding: "8px 10px", borderRadius: 6,
                                background: "var(--bg-primary)", border: "1px solid var(--border)"
                            }}>
                                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{check.label}</div>
                                <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                                    <button
                                        onClick={() => canManageReview && handleObservationChange(check.key, "isCompliant", isYes ? null : true)}
                                        disabled={!canManageReview}
                                        style={{
                                            padding: "3px 12px", fontSize: 11, fontWeight: 600,
                                            cursor: canManageReview ? "pointer" : "default",
                                            border: "1px solid " + (isYes ? "#22c55e" : "var(--border)"),
                                            borderRadius: 4,
                                            background: isYes ? "rgba(34,197,94,0.15)" : "transparent",
                                            color: isYes ? "#22c55e" : "var(--text-muted)",
                                            opacity: canManageReview ? 1 : 0.6
                                        }}
                                    >Yes</button>
                                    <button
                                        onClick={() => canManageReview && handleObservationChange(check.key, "isCompliant", isNo ? null : false)}
                                        disabled={!canManageReview}
                                        style={{
                                            padding: "3px 12px", fontSize: 11, fontWeight: 600,
                                            cursor: canManageReview ? "pointer" : "default",
                                            border: "1px solid " + (isNo ? "#ef4444" : "var(--border)"),
                                            borderRadius: 4,
                                            background: isNo ? "rgba(239,68,68,0.15)" : "transparent",
                                            color: isNo ? "#ef4444" : "var(--text-muted)",
                                            opacity: canManageReview ? 1 : 0.6
                                        }}
                                    >No</button>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Notes..."
                                    value={obs.notes || ""}
                                    onChange={(e) => canManageReview && handleObservationChange(check.key, "notes", e.target.value)}
                                    readOnly={!canManageReview}
                                    style={{
                                        width: "100%", fontSize: 11, padding: "3px 6px",
                                        border: "1px solid var(--border)", borderRadius: 3,
                                        background: "var(--bg-primary)", color: "var(--text-primary)",
                                        boxSizing: "border-box",
                                        opacity: canManageReview ? 1 : 0.6
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Preset grid */}
            <div style={{ padding: "20px" }}>
                {scanDetail.presets && scanDetail.presets.length > 0 ? (
                    <div className="scan-preset-grid">
                        {scanDetail.presets.map((p) => {
                            const presetKey = `preset:${p.id}`;
                            return (
                                <div key={p.id} id={presetCardDomId(p.presetName)} className="scan-preset-card">
                                    {p.imageUrl ? (
                                        <img
                                            src={p.imageUrl}
                                            alt={p.presetName}
                                            className="scan-preset-image"
                                            onClick={() => {
                                                const allImages = scanDetail.presets
                                                    .filter((x) => x.imageUrl)
                                                    .map((x) => ({ url: x.imageUrl, label: `${x.presetName} — ${x.fixtureType ? x.fixtureType.replace(/_/g, " ") : x.checkType}` }));
                                                const idx = allImages.findIndex((x) => x.url === p.imageUrl);
                                                setModalImages(allImages);
                                                setModalIndex(idx >= 0 ? idx : 0);
                                            }}
                                        />
                                    ) : (
                                        <div className="scan-preset-no-image">No image</div>
                                    )}

                                    {/* Open team-ticket notice: this fault was routed away
                                        from the site (e.g. to R&M) and that ticket is still
                                        open. Do not re-raise/reopen for the same fault. */}
                                    {p.openTeamTickets && p.openTeamTickets.length > 0 && (
                                        <div style={{
                                            padding: "7px 10px", fontSize: 12, fontWeight: 700,
                                            background: "rgba(249,115,22,0.14)",
                                            borderBottom: "2px solid #f97316",
                                            color: "#f97316",
                                        }}>
                                            {p.openTeamTickets.map((t) => (
                                                <div key={t.id}>
                                                    OPEN {(t.team || "TEAM").toUpperCase()} TICKET #{t.id}
                                                    {t.title ? ` — ${t.title}` : ""}
                                                    <span style={{ fontWeight: 400, color: "var(--text-secondary)", display: "block", fontSize: 11 }}>
                                                        Already with {t.team || "another team"} — don't re-raise or reopen this fault. A different, new issue can still be raised to Site.
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Issue history bar */}
                                    {(p.openIssueCount > 0 || p.totalIssueCount > 0) && (
                                        <div style={{
                                            display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
                                            padding: "5px 10px", fontSize: 11, fontWeight: 600,
                                            background: p.openIssueCount > 0 ? "rgba(239,68,68,0.08)" : "var(--bg-secondary)",
                                            borderBottom: "1px solid var(--border)"
                                        }}>
                                            {p.openIssueCount > 0 && (
                                                <span style={{
                                                    color: "#ef4444", background: "rgba(239,68,68,0.12)",
                                                    padding: "1px 7px", borderRadius: 10
                                                }}>
                                                    {p.openIssueCount} open
                                                </span>
                                            )}
                                            {p.totalIssueCount > 0 && (
                                                <span style={{ color: "var(--text-muted)" }}>
                                                    {p.totalIssueCount} total issue{p.totalIssueCount !== 1 ? "s" : ""} raised
                                                </span>
                                            )}
                                            {p.totalReopens > 0 && (
                                                <span style={{
                                                    color: "#f97316", background: "rgba(249,115,22,0.1)",
                                                    padding: "1px 7px", borderRadius: 10
                                                }}>
                                                    {p.totalReopens} reopen{p.totalReopens !== 1 ? "s" : ""}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    <div className="scan-preset-info">
                                        <div className="scan-preset-header">
                                            <strong>{p.presetName}</strong>
                                            <ScoreBadge score={p.score} />
                                        </div>
                                        <div className="scan-preset-type">{p.fixtureType ? p.fixtureType.replace(/_/g, " ") : p.checkType}</div>
                                        {p.summary && (
                                            <div className="scan-preset-summary">{p.summary}</div>
                                        )}
                                        {p.findings && p.findings.length > 0 && (
                                            <div className="scan-preset-findings">
                                                {p.findings.map((f) => (
                                                    <div key={f.id} className="scan-finding-item" style={{ marginBottom: 4 }}>
                                                        <span className="scan-finding-severity" style={{
                                                            color: severityColor[f.severity] || "var(--text-secondary)"
                                                        }}>
                                                            {f.severity}
                                                        </span>
                                                        <span className="scan-finding-title" style={{ marginLeft: 6 }}>{f.title}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <PresetNotesPanel preset={p} siteId={siteId} reviewerEmail={reviewerEmail} />

                                        <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid var(--border)" }}>
                                            {issueResults[presetKey] ? (
                                                <IssueCreatedBanner result={issueResults[presetKey]} />
                                            ) : creatingIssueFor === presetKey ? (
                                                <CreateIssueForm
                                                    preset={p}
                                                    siteId={siteId}
                                                    scanHour={scanHour}
                                                    onCreated={(result) => handleIssueCreated(presetKey, result)}
                                                    onCancel={() => setCreatingIssueFor(null)}
                                                />
                                            ) : canCreateIssue ? (
                                                <button
                                                    className="raise-issue-btn raise-issue-btn-primary"
                                                    onClick={() => setCreatingIssueFor(presetKey)}
                                                >
                                                    {/* Signals that opening the form costs a click,
                                                        not a rewrite -- the reviewer's job is to
                                                        agree or correct, not to compose. */}
                                                    {p.suggestion ? "Review & Raise Issue" : "Raise Issue"}
                                                    {p.suggestion && (
                                                        <span style={{
                                                            marginLeft: 6, fontSize: 10, fontWeight: 700,
                                                            padding: "1px 6px", borderRadius: 8,
                                                            background: "rgba(255,255,255,0.22)",
                                                        }}>
                                                            PRE-FILLED
                                                        </span>
                                                    )}
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="empty-state"><p>No preset data for this scan.</p></div>
                )}
            </div>

            {/* Bottom close bar */}
            <div style={{
                padding: "16px 20px", borderTop: "1px solid var(--border)",
                display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6
            }}>
                <button
                    onClick={() => setShowCloseDialog(true)}
                    disabled={completing}
                    style={{
                        padding: "8px 20px", fontSize: 13, fontWeight: 600,
                        background: completing ? "#aaa" : "#22c55e", color: "#fff",
                        border: "none", borderRadius: 6, cursor: completing ? "default" : "pointer"
                    }}
                >
                    {completing ? "Saving..." : "Close Review"}
                </button>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {CLOSE_REVIEW_EMAIL_NOTE}
                </div>
            </div>

            {showCloseDialog && (
                <div style={{
                    position: "fixed", inset: 0, zIndex: 200,
                    background: "rgba(0,0,0,0.5)",
                    display: "flex", alignItems: "center", justifyContent: "center"
                }}>
                    <div style={{
                        background: "var(--bg-primary)", borderRadius: 10,
                        padding: "28px 32px", maxWidth: 400, width: "90%",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.2)"
                    }}>
                        <h3 style={{ margin: "0 0 8px", fontSize: 17 }}>Close Review</h3>
                        <p style={{ margin: "0 0 20px", color: "var(--text-secondary)", fontSize: 14 }}>
                            Is this review complete?
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            <button
                                onClick={() => handleClose(true)}
                                style={{
                                    padding: "10px 16px", fontSize: 14, fontWeight: 600,
                                    background: "#22c55e", color: "#fff",
                                    border: "none", borderRadius: 6, cursor: "pointer", textAlign: "left"
                                }}
                            >
                                Yes — mark as reviewed & close
                            </button>
                            <button
                                onClick={() => handleClose(false)}
                                style={{
                                    padding: "10px 16px", fontSize: 14, fontWeight: 600,
                                    background: "#3b82f6", color: "#fff",
                                    border: "none", borderRadius: 6, cursor: "pointer", textAlign: "left"
                                }}
                            >
                                No — keep as in progress & close
                            </button>
                            <button
                                onClick={handleAbandon}
                                style={{
                                    padding: "10px 16px", fontSize: 14,
                                    background: "transparent", color: "var(--text-secondary)",
                                    border: "1px solid var(--border)", borderRadius: 6, cursor: "pointer", textAlign: "left"
                                }}
                            >
                                Close without reviewing
                            </button>
                            <button
                                onClick={() => setShowCloseDialog(false)}
                                style={{
                                    padding: "8px 16px", fontSize: 13,
                                    background: "transparent", color: "var(--text-muted)",
                                    border: "none", borderRadius: 6, cursor: "pointer"
                                }}
                            >
                                Cancel — keep reviewing
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {modalImages && (
                <ImageModal
                    images={modalImages}
                    currentIndex={modalIndex}
                    onClose={() => setModalImages(null)}
                    onPrev={modalIndex > 0 ? () => setModalIndex(modalIndex - 1) : null}
                    onNext={modalIndex < modalImages.length - 1 ? () => setModalIndex(modalIndex + 1) : null}
                />
            )}
        </div>
    );
}

export default ScanReviewPage;
